import React from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import {
  FaFileUpload,
  FaTrashAlt,
  FaExclamationCircle,
  FaCheckCircle,
  FaLink,
} from 'react-icons/fa';

import AudioIcon from '~/components/Icons/Files/Audio';
import PdfIcon from '~/components/Icons/Files/Pdf';
import PresentationIcon from '~/components/Icons/Files/Presentation';
import SpreadsheetIcon from '~/components/Icons/Files/Spreadsheet';
import TextIcon from '~/components/Icons/Files/Text';
import VideoIcon from '~/components/Icons/Files/Video';
import WordIcon from '~/components/Icons/Files/Word';

import { useCompletionFiles, ICompletionFile } from '~/context/completionfiles';

import {
  Container,
  FileRow,
  FileContainer,
  FilePreviewWrapper,
  FilePreviewImageWrapper,
  FilePreviewImage,
  FileInfoWrapper,
  FileInfoName,
  FileInfoFileSize,
  FileActions,
  DeleteBtn,
  UrlIcon,
  SuccessIcon,
  ErrorIcon,
} from './styles';

const List = (): JSX.Element => {
  const { uploadedCompletionFiles: completionFiles, deleteCompletionFile } =
    useCompletionFiles();

  if (!completionFiles.length) {
    return (
      <span>
        <FaFileUpload
          style={{ marginLeft: '45%', marginTop: 15 }}
          size={35}
          color="#adadad"
        />
      </span>
    );
  }

  function handleCompletionFilePreview(
    uploadedCompletionFile: any,
  ): React.ReactNode {
    if (
      uploadedCompletionFile.type === 'audio/mp3' ||
      uploadedCompletionFile.type === 'audio/mpeg'
    ) {
      return <AudioIcon />;
    }

    if (uploadedCompletionFile.type === 'application/pdf') {
      return <PdfIcon />;
    }

    if (
      uploadedCompletionFile.type ===
        'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      uploadedCompletionFile.type ===
        'application/vnd.oasis.opendocument.presentation'
    ) {
      return <PresentationIcon />;
    }

    if (
      uploadedCompletionFile.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      uploadedCompletionFile.type ===
        'application/vnd.oasis.opendocument.spreadsheet'
    ) {
      return <SpreadsheetIcon />;
    }

    if (uploadedCompletionFile.type === 'text/plain') {
      return <TextIcon />;
    }

    if (
      uploadedCompletionFile.type === 'image/gif' ||
      uploadedCompletionFile.type === 'video/quicktime' ||
      uploadedCompletionFile.type === 'video/mp4'
    ) {
      return <VideoIcon />;
    }

    if (
      uploadedCompletionFile.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      uploadedCompletionFile.type === 'application/vnd.oasis.opendocument.text'
    ) {
      return <WordIcon />;
    }

    return (
      <FilePreviewImageWrapper>
        <FilePreviewImage src={uploadedCompletionFile.preview} />
      </FilePreviewImageWrapper>
    );
  }

  return (
    <Container>
      {completionFiles.map((uploadedCompletionFile: ICompletionFile) => (
        <FileRow key={uploadedCompletionFile.id}>
          <FileContainer>
            <FilePreviewWrapper>
              {handleCompletionFilePreview(uploadedCompletionFile)}
            </FilePreviewWrapper>

            <FileInfoWrapper>
              <FileInfoName>{uploadedCompletionFile.name}</FileInfoName>

              <FileInfoFileSize>
                {uploadedCompletionFile.readableSize}
              </FileInfoFileSize>
            </FileInfoWrapper>
          </FileContainer>

          <FileActions>
            {!uploadedCompletionFile.uploaded && !uploadedCompletionFile.error && (
              <CircularProgressbar
                styles={{
                  root: { width: 24 },
                  path: { stroke: '#7159c1' },
                }}
                strokeWidth={10}
                text={String(uploadedCompletionFile.progress)}
                value={uploadedCompletionFile.progress || 0}
              />
            )}

            {uploadedCompletionFile.url && (
              <UrlIcon>
                <a
                  href={uploadedCompletionFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaLink size={20} />
                </a>
              </UrlIcon>
            )}

            {uploadedCompletionFile.uploaded && (
              <SuccessIcon>
                <FaCheckCircle size={24} />
              </SuccessIcon>
            )}
            {uploadedCompletionFile.error && (
              <ErrorIcon>
                <FaExclamationCircle size={24} />
              </ErrorIcon>
            )}
            {!!uploadedCompletionFile.url && (
              <DeleteBtn
                onClick={() => deleteCompletionFile(uploadedCompletionFile.id)}
              >
                <FaTrashAlt size={24} />
              </DeleteBtn>
            )}
          </FileActions>
        </FileRow>
      ))}
    </Container>
  );
};

export default List;
